import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { swrKeys } from '@shared/api/swr/keys';
import { swrPaginatedListConfig } from '@shared/api/swr/list-config';
import type { ServiceAccountingsResponse } from '@shared/api/contracts';
import {
  DEFAULT_FILTERS,
  mapServiceAccountingsResponse,
  toServiceAccountingsQuery,
  type ServiceAccountingFilters,
} from '@entities/service-accounting';

const DEFAULT_LIMIT = 35;
const SEARCH_DEBOUNCE_MS = 300;

const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);

  return debounced;
};

export const useServiceAccountings = (
  page = 1,
  limit = DEFAULT_LIMIT,
  filters: ServiceAccountingFilters = DEFAULT_FILTERS,
) => {
  const debouncedSearch = useDebouncedValue(filters.search, SEARCH_DEBOUNCE_MS);

  const queryParams = useMemo(() => {
    const filterQuery = toServiceAccountingsQuery({
      ...filters,
      search: debouncedSearch,
    });

    return {
      page,
      limit,
      ...filterQuery,
    };
  }, [page, limit, filters, debouncedSearch]);

  const swrKey = useMemo(() => swrKeys.accounting.service(queryParams), [queryParams]);

  const { data, error, isLoading, mutate } = useSWR<ServiceAccountingsResponse>(
    swrKey,
    swrPaginatedListConfig,
  );

  const { operations, summary } = useMemo(
    () => mapServiceAccountingsResponse(data),
    [data],
  );

  const pagination = {
    page: Number(data?.pagination?.page ?? page),
    limit: Number(data?.pagination?.limit ?? limit),
    total: Number(data?.pagination?.total ?? 0),
    totalPages: Number(
      data?.pagination?.totalPages ??
        (Math.ceil(Number(data?.pagination?.total ?? 0) / limit) || 1),
    ),
  };

  return {
    operations,
    summary,
    pagination,
    isLoading: isLoading && !data,
    error,
    mutate,
  };
};
